/**
 * Tenant Settings screen (DNKADM-0050) — the v2 tenant-wide booking config + the
 * setup-only currency editor (handoff §5.1). `RequireAdmin`-gated route + a
 * Configuration nav item ("Tenant settings"). This is a NEW v2 screen (it mirrors
 * the v1 `settings/SettingsScreen` load/validate/save/toast pattern, not a re-point
 * of it).
 *
 * Loads `GET /v2/admin/tenant-settings` through `useResource` (loading / ready /
 * error, fail-closed retry) and renders a failure through the shared `ScreenError`
 * (DNKADM-0440): a `403` is the access notice naming the roles (the route is also
 * `RequireAdmin`-gated; this covers a server-side denial). Two
 * cards on `ready`:
 *  - `TenantSettingsCard` — the booking-config form (`PUT /tenant-settings`, never
 *    sends `currency`);
 *  - `CurrencyCard` — the setup-only currency editor with `CURRENCY_LOCKED`
 *    handling.
 * A third `AskDinkCard` (DNKADM-0461) holds the per-business Ask Dink switch and saves on its own.
 * A `CoachHoursCard` (DNKADM-0490) holds the "Coaches can set their own hours" switch and
 * saves on its own the same way.
 * A `PoweredByCard` (DNKADM-0500) holds the "Powered by Dink" link switch, saved the same way.
 * Each card is remounted on every successful (re)load (keyed on the loaded view) so
 * its inputs reseed from the freshly loaded data after a refetch-after-write.
 */
import { useCallback } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { roleLimitedMessage } from '@/components/accessCopy';
import { getTenantSettings } from '@/api/tenantSettings';
import { TenantSettingsCard } from './TenantSettingsCard';
import { CurrencyCard } from './CurrencyCard';
import { AskDinkCard } from './AskDinkCard';
import { CoachHoursCard } from './CoachHoursCard';
import { PoweredByCard } from './PoweredByCard';
import { ScreenError } from '@/components/ScreenError';

export function TenantSettingsScreen() {
  const showToast = useToast();
  const settings = useResource(useCallback(() => getTenantSettings(), []));

  return (
    <Box>
      <Typography variant="h1">Tenant settings</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5 }}>
        Tenant-wide booking settings and the tenant currency. Currency can be set during setup and
        locks on the first reservation or payment.
      </Typography>

      {settings.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={420}
          role="status"
          aria-label="Loading tenant settings"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {settings.status === 'error' && (
        <ScreenError
          error={settings.error}
          what="the tenant settings"
          onRetry={settings.refetch}
          forbiddenMessage={roleLimitedMessage('Tenant settings', 'OWNER or ADMIN')}
          sx={{ maxWidth: 560 }}
        />
      )}

      {settings.status === 'ready' && settings.data && (
        <Stack spacing={3}>
          <TenantSettingsCard
            // Remount on each (re)load so inputs reseed from the fresh view.
            key={`s|${settings.data.serviceIntervalMinutes}|${settings.data.bookingWindowMonths}|${settings.data.guestHoldTtlMinutes}|${settings.data.defaultTimezone}|${settings.data.billingAnchorDay}|${settings.data.staffCalendarProvider}`}
            data={settings.data}
            refetch={settings.refetch}
            showToast={showToast}
          />
          <AskDinkCard
            // Never refetches the screen (that would drop unsaved edits in the form above).
            initialEnabled={settings.data.assistantEnabled}
            showToast={showToast}
          />
          <CoachHoursCard
            // Saves on its own; never refetches the screen (same reason as Ask Dink).
            initialEnabled={settings.data.coachesSetOwnHours}
            showToast={showToast}
          />
          <PoweredByCard
            // Saves on its own; never refetches the screen (same reason as Ask Dink).
            initialEnabled={settings.data.poweredByLinkEnabled}
            showToast={showToast}
          />
          <CurrencyCard
            key={`c|${settings.data.currency}|${String(settings.data.currencyLocked)}`}
            data={settings.data}
            refetch={settings.refetch}
            showToast={showToast}
          />
        </Stack>
      )}
    </Box>
  );
}
